// Hideo - Calendar store: the view (Month | Week, device preference "creatorOS_calview"), the month and the week shown
// (also chosen from the title's menu, CalJump.vue: months, years, lustra, decades) and the animation of the next drawing (src/features/calendar.ts; page src/pages/CalendarPage.vue).
// The period survives page changes (like the legacy page, which was only hidden). Saving a video with a date moves the
// Calendar to it (video store save → showDate).
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useVideoStore } from './video'
import { CAL_VIEW_KEY, jumpPeriod, navPeriod, parseCalView, switchPeriod, type CalAnim, type CalView } from '../features/calendar'
import { dayOf, firstOfMonth, mondayOf } from '../utils/date'

function readView(): CalView {
  try { return parseCalView(localStorage.getItem(CAL_VIEW_KEY)) } catch { return 'month' }
}
function storeView(v: CalView): void {
  try { localStorage.setItem(CAL_VIEW_KEY, v) } catch { /* storage full or blocked */ }
}

export const useCalendarStore = defineStore('calendar', () => {
  const view = ref<CalView>(readView())
  /** First day of the month shown */
  const month = ref(firstOfMonth(new Date()))
  /** Monday of the week shown */
  const week = ref(mondayOf(new Date()))
  /** Animation of the next drawing (the page plays it and clears it): only when the period or the view changes */
  const anim = ref<CalAnim>('')

  function setView(v: string): void {
    anim.value = 'in'
    view.value = v === 'week' ? 'week' : 'month'
    storeView(view.value)
    const p = switchPeriod(view.value, { month: month.value, week: week.value }, new Date())
    month.value = p.month
    week.value = p.week
  }
  /** Previous (-1) or next (1) month or week */
  function nav(d: number): void {
    anim.value = d > 0 ? 'next' : 'prev'
    const p = navPeriod(view.value, { month: month.value, week: week.value }, d)
    month.value = p.month
    week.value = p.week
  }
  function today(): void {
    anim.value = 'in'
    month.value = firstOfMonth(new Date())
    week.value = mondayOf(new Date())
  }
  /** The title's menu (CalJump.vue): a month of a year (0–11), in the view shown */
  function jump(year: number, m: number): void {
    anim.value = 'in'
    const p = jumpPeriod(year, m, new Date())
    month.value = p.month
    week.value = p.week
  }
  /** "+N more" on a day of the month (or a tap on a phone): the week of that day */
  function openWeek(iso: string): void {
    anim.value = 'in'
    week.value = mondayOf(dayOf(iso))
    view.value = 'week'
    storeView('week')
  }
  /** A video saved with this date: the month and the week of that day (no animation) */
  function showDate(iso: string): void {
    const d = dayOf(iso)
    month.value = firstOfMonth(d)
    week.value = mondayOf(d)
  }
  /** New video on a day (and at a time) of the Calendar: the video dialog with the date already filled in */
  function newVideoOn(iso: string, time = ''): void {
    useVideoStore().open(null, time ? { publishDate: iso, publishTime: time } : { publishDate: iso })
  }

  return { view, month, week, anim, setView, nav, today, jump, openWeek, showDate, newVideoOn }
})
